import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatPreferredDate } from '../../utils/preferredDate';
import '@fullcalendar/react/skeleton.css';
import '@fullcalendar/react/themes/classic/theme.css';
import '@fullcalendar/react/themes/classic/palette.css';
import classicThemePlugin from '@fullcalendar/react/themes/classic';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/react/daygrid';
import timeGridPlugin from '@fullcalendar/react/timegrid';
import listPlugin from '@fullcalendar/react/list';
import { API_BASE_URL } from '../../utils/apiBase';
import { ShootMap, PlaceSearch } from '../Common/ShootLocationField';
import {
  addMinutesToClock,
  confirmationStartTime,
  durationBetween,
  ensureDurationOption,
  ensureTimeOption,
  formatDurationLabel,
} from '../../utils/bookingSchedule';
import { studioDate } from '../../utils/studioDate';
import { deliveryCountdown, studioToday } from '../../utils/clientJourney';
import ClientSendPrompt from './ClientSendPrompt';
import NotifyChannelPrompt from './NotifyChannelPrompt';
import {
  bookingUpdateWhatsAppText,
  buildStudioWhatsAppUrl,
} from '../../utils/studioNotifyChannels';

export interface Booking {
  id?: string;
  quoteId: string;
  quoteNumber?: string;
  clientName?: string;
  shootType?: string;
  shootDate?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  locationName?: string;
  address?: string;
  mapsUrl?: string;
  dateConfirmed?: boolean;
  completedAt?: string;
  completedOn?: string;
  /** Promised date for the edited photographs; set when the shoot is marked done. */
  deliveryDueOn?: string;
  deliveredAt?: string;
  deliveryLink?: string;
  deliveryNote?: string;
  closedAt?: string;
  closeNote?: string;
  proposedShootDate?: string;
  proposedStartTime?: string;
  proposedEndTime?: string;
  internalNotes?: string;
  clientNotes?: string;
  clash?: boolean;
  blocked?: boolean;
  blockReason?: string;
  deletedAt?: string | null;
  archivedAt?: string | null;
  isTest?: boolean;
  /** Present when the calendar expands a multi-day booking into one event per session. */
  sessionId?: string;
  sessionIndex?: number;
  sessionCount?: number;
  sessionLabel?: string;
  sessions?: Array<{
    id?: string;
    label?: string;
    shootDate?: string;
    startTime?: string;
    endTime?: string;
    durationMinutes?: number;
    locationName?: string;
    address?: string;
    mapsUrl?: string;
    confirmed?: boolean;
  }>;
}

interface Block {
  id: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

type ViewName = 'dayGridMonth' | 'timeGridWeek' | 'agenda' | 'list' | 'unscheduled';

const views: Array<[ViewName, string]> = [
  ['dayGridMonth', 'Month'],
  ['timeGridWeek', 'Week'],
  ['agenda', 'Agenda'],
  ['list', 'List'],
  ['unscheduled', 'Unscheduled'],
];

function minutes(value?: string) {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '');
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function clock(total: number) {
  const hours = Math.max(0, Math.min(23, Math.floor(total / 60)));
  return `${String(hours).padStart(2, '0')}:00:00`;
}

function nextDay(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  return date.toISOString().slice(0, 10);
}

function bookingState(booking: Booking) {
  if (booking.closedAt) return 'Closed';
  if (booking.deliveredAt) return 'Delivered';
  if (booking.completedAt) return 'Shoot done · editing';
  if (booking.dateConfirmed) return 'Confirmed';
  if (booking.shootDate) return 'Proposed';
  return 'Needs a date';
}

type DayDraft = {
  id: string;
  label: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  locationName: string;
  address: string;
  mapsUrl: string;
  confirmed: boolean;
};

type AskedSession = {
  id?: string;
  label?: string;
  date?: string;
  startTime?: string;
  durationMinutes?: number;
  location?: { name?: string; address?: string; mapsUrl?: string } | null;
};

function mergeDay(
  index: number,
  asked?: AskedSession | null,
  saved?: NonNullable<Booking['sessions']>[number] | null,
  shootAlreadyConfirmed = false,
): DayDraft {
  const place = asked?.location || {};
  const start = String(saved?.startTime || asked?.startTime || '').slice(0, 5);
  const savedDuration = Number(saved?.durationMinutes) || durationBetween(start, saved?.endTime) || 0;
  const askedDuration = Number(asked?.durationMinutes) || 0;
  const date = saved?.shootDate || asked?.date || '';
  return {
    id: saved?.id || asked?.id || `session-${index + 1}`,
    label: String(saved?.label || asked?.label || '').trim() || `Day ${index + 1}`,
    date,
    startTime: start,
    durationMinutes: savedDuration || askedDuration || 60,
    locationName: saved?.locationName || place.name || '',
    address: saved?.address || place.address || '',
    mapsUrl: saved?.mapsUrl || place.mapsUrl || '',
    confirmed: Boolean(saved?.confirmed) || (shootAlreadyConfirmed && Boolean(date && start)),
  };
}

function daysFromBooking(booking: Booking): DayDraft[] | null {
  const saved = booking.sessions || [];
  return saved.length > 1
    ? saved.map((session, index) => mergeDay(index, null, session, Boolean(booking.dateConfirmed)))
    : null;
}

/** Shoot done, photographs not yet delivered: the client is waiting on Carmen. */
function isEditing(booking: Booking) {
  return Boolean(booking.completedAt && !booking.deliveredAt && !booking.closedAt);
}

/**
 * One calendar mark for an editing job: a red dot on the day the photographs are due.
 * Rolling over it shows who is waiting. Days in between are left clear.
 */
function editingEvents(booking: Booking, today: string) {
  if (!isEditing(booking)) return [];
  const due = /^\d{4}-\d{2}-\d{2}$/.test(String(booking.deliveryDueOn || '')) ? String(booking.deliveryDueOn) : '';
  if (!due) return [];
  const countdown = deliveryCountdown(due, today);
  const name = chipClientName(booking.clientName);
  const daysText = countdown.daysLeft == null
    ? ''
    : countdown.overdue
      ? `${Math.abs(countdown.daysLeft)}d over`
      : countdown.daysLeft === 0 ? 'due today' : `${countdown.daysLeft}d left`;
  const overdueClass = countdown.overdue ? ' is-overdue' : '';
  const detail = [
    booking.shootType || '',
    booking.shootDate ? `Shot ${studioDate(booking.shootDate)}` : '',
    `Due ${studioDate(due)}`,
    countdown.text,
    booking.isTest ? 'Test job' : '',
    booking.quoteNumber || '',
  ].filter(Boolean);
  const dueTitle = [`Deliver photographs — ${booking.clientName || 'Client'}`, ...detail].join('\n');
  return [{
    id: `editing-due-${booking.quoteId}`,
    title: `Deliver · ${name}${daysText ? ` · ${daysText}` : ''}`,
    start: due,
    allDay: true,
    display: 'list-item',
    className: `is-editing-dot is-delivery-dot${overdueClass}`,
    extendedProps: { ...booking, daysText, countdownText: countdown.text, overdue: countdown.overdue, longTitle: dueTitle, editingKind: 'due' },
  }];
}

function flags(booking: Booking) {
  return [booking.clash ? 'Overlap' : '', booking.blocked ? 'Blocked' : ''].filter(Boolean);
}

function chipClientName(value?: string) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Client';
  if (parts.length === 1) return parts[0];
  // Prefer first name — last-word chips misread fixture names like "Nina Confirmed".
  return parts[0];
}

function escapeHtml(value: string) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function shortShoot(value?: string) {
  const shoot = String(value || 'Shoot').trim();
  if (shoot.length <= 22) return shoot;
  return `${shoot.slice(0, 20).trim()}…`;
}

function appointmentTone(booking: Booking) {
  if (!booking.completedAt && !booking.dateConfirmed && !booking.shootDate) {
    return [booking.clash ? 'is-overlap' : '', booking.blocked ? 'is-blocked' : ''].filter(Boolean);
  }
  const tone = booking.completedAt ? 'is-complete' : booking.dateConfirmed ? 'is-confirmed' : 'is-proposed';
  return [tone, booking.clash ? 'is-overlap' : '', booking.blocked ? 'is-blocked' : ''].filter(Boolean);
}

function eventChip(booking: Booking) {
  const time = booking.startTime ? booking.startTime.slice(0, 5) : '';
  const name = chipClientName(booking.clientName);
  const shoot = shortShoot(booking.shootType);
  const state = booking.closedAt ? 'Closed' : booking.deliveredAt ? 'Delivered' : booking.completedAt ? 'Done' : booking.dateConfirmed ? 'Confirmed' : 'Proposed';
  return { time, name, shoot, state, warnings: flags(booking) };
}

function eventLabel(booking: Booking) {
  const chip = eventChip(booking);
  const day = booking.sessionCount && booking.sessionCount > 1
    ? (booking.sessionLabel || `Day ${(booking.sessionIndex || 0) + 1} of ${booking.sessionCount}`)
    : '';
  return [chip.time, chip.name, day, chip.shoot].filter(Boolean).join(' · ');
}

export interface BookingFollowUp {
  emailStatus?: string;
  kind: 'confirmed' | 'updated';
  shootDate?: string;
  startTime?: string;
  locationName?: string;
  address?: string;
  mapsUrl?: string;
  quoteNumber?: string;
  link?: string;
  failedNoticeId?: string;
  noticeLine?: string;
  sessions?: Booking['sessions'];
}

export const BookingEditor = ({
  booking,
  onOpenQuotation,
  onSaved,
  clientPhone,
  clientEmail,
  clientPreference,
  embedded = false,
  followUp = null,
  onFollowUpHandled,
  preferredTime = '',
}: {
  booking: Booking;
  /** Shown inside a client page: the client and quotation are already in view. */
  embedded?: boolean;
  onOpenQuotation?: (quoteId: string) => void;
  onSaved: (booking: Booking, note: string) => void;
  clientPhone?: string;
  clientEmail?: string;
  clientPreference?: string;
  /** A confirm that happened on the shoot file, so the same WhatsApp offer still appears here. */
  followUp?: BookingFollowUp | null;
  onFollowUpHandled?: () => void;
  /** Time already suggested on the quotation. Fills the confirmation until a booking time is saved. */
  preferredTime?: string;
}) => {
  const existingStart = booking.proposedStartTime || booking.startTime || preferredTime || '';
  const existingEnd = booking.proposedEndTime || booking.endTime || '';
  const existingDuration = durationBetween(existingStart, existingEnd);
  const [proposalDate, setProposalDate] = useState(booking.proposedShootDate || '');
  const [proposalStart, setProposalStart] = useState(() => confirmationStartTime(booking, preferredTime));
  const [durationMinutes, setDurationMinutes] = useState(existingDuration || 60);
  const [packageDurationMinutes, setPackageDurationMinutes] = useState<number | null>(null);
  const [requestedDate, setRequestedDate] = useState('');
  const [fetchedAsk, setFetchedAsk] = useState<{ id: string; time: string } | null>(null);
  const startEdited = useRef(false);
  const [days, setDays] = useState<DayDraft[] | null>(() => daysFromBooking(booking));
  const daysTouched = useRef(false);
  const [locationName, setLocationName] = useState(booking.locationName || '');
  const [address, setAddress] = useState(booking.address || '');
  const [mapsUrl, setMapsUrl] = useState(booking.mapsUrl || '');
  const [error, setError] = useState('');
  const proposalEnd = addMinutesToClock(proposalStart, durationMinutes);

  useEffect(() => {
    startEdited.current = false;
    daysTouched.current = false;
    setDays(daysFromBooking(booking));
  }, [booking.quoteId]);

  const suggestedTime = (fetchedAsk?.id === booking.quoteId && fetchedAsk.time)
    ? fetchedAsk.time
    : confirmationStartTime(null, preferredTime);

  useEffect(() => {
    // A waiting change fills the form, so one click confirms what is on screen.
    // Otherwise the form shows the saved date.
    const start = booking.proposedStartTime || booking.startTime || '';
    const end = booking.proposedEndTime || booking.endTime || '';
    const span = durationBetween(start, end);
    const nextDate = booking.proposedShootDate || booking.shootDate || '';
    const nextDuration = span || 60;
    setProposalDate((current) => (current === nextDate ? current : nextDate));
    setDurationMinutes((current) => (current === nextDuration ? current : nextDuration));
    setLocationName(booking.locationName || '');
    setAddress(booking.address || '');
    setMapsUrl(booking.mapsUrl || '');
    setPackageDurationMinutes(null);
  }, [booking.quoteId, booking.dateConfirmed, booking.shootDate, booking.proposedShootDate, booking.proposedStartTime, booking.proposedEndTime, booking.startTime, booking.endTime, booking.locationName, booking.address, booking.mapsUrl]);

  useEffect(() => {
    if (startEdited.current) return;
    const nextStart = confirmationStartTime(
      { proposedStartTime: booking.proposedStartTime, startTime: booking.startTime },
      suggestedTime,
    );
    setProposalStart((current) => (current === nextStart ? current : nextStart));
  }, [booking.quoteId, booking.proposedStartTime, booking.startTime, suggestedTime]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const quoteResponse = await fetch(`${API_BASE_URL}/quotes/${booking.quoteId}`);
        if (!quoteResponse.ok) return;
        const quote = await quoteResponse.json();
        if (active) {
          setRequestedDate(quote?.preferredDate || '');
          setFetchedAsk({ id: booking.quoteId, time: confirmationStartTime(null, quote?.preferredTime) });
          const asked = Array.isArray(quote?.sessions) ? quote.sessions as AskedSession[] : [];
          const saved = Array.isArray(booking.sessions) ? booking.sessions : [];
          const dayCount = Math.max(asked.length, saved.length);
          if (!daysTouched.current && dayCount > 1) {
            setDays(Array.from({ length: dayCount }, (_, index) => mergeDay(index, asked[index], saved[index], Boolean(booking.dateConfirmed))));
          } else if (
            !daysTouched.current
            && !booking.dateConfirmed
            && !booking.shootDate
            && !booking.proposedShootDate
            && quote?.preferredDate
          ) {
            setProposalDate((current) => current || String(quote.preferredDate));
          }
        }
        const preferred = quote?.preferredLocation || {};
        if (active && !booking.locationName && !booking.address && !booking.mapsUrl && (preferred.name || preferred.address || preferred.mapsUrl)) {
          setLocationName(preferred.name || '');
          setAddress(preferred.address || '');
          setMapsUrl(preferred.mapsUrl || '');
        }
        const packageLine = Array.isArray(quote?.snapshot?.lines)
          ? quote.snapshot.lines.find((line: { kind?: string; id?: string; pricingType?: string; units?: number }) => line.kind === 'package')
          : null;
        const packageId = packageLine?.id || quote?.draft?.packageId || '';
        let minutes = 0;
        if (packageId) {
          const servicesResponse = await fetch(`${API_BASE_URL}/services`);
          if (servicesResponse.ok) {
            const services = await servicesResponse.json();
            const match = Array.isArray(services) ? services.find((item: { id?: string }) => item.id === packageId) : null;
            minutes = Number(match?.durationMinutes) || 0;
          }
        } else if (packageLine?.pricingType === 'per-hour' && Number(packageLine.units) > 0) {
          minutes = Math.round(Number(packageLine.units) * 60);
        }
        if (!active || !Number.isFinite(minutes) || minutes <= 0) return;
        const rounded = Math.round(minutes / 30) * 30;
        setPackageDurationMinutes(rounded);
        const start = booking.proposedStartTime || booking.startTime || '';
        const end = booking.proposedEndTime || booking.endTime || '';
        if (!durationBetween(start, end)) setDurationMinutes(rounded);
      } catch {
        // Keep the manual duration controls if the package cannot be loaded.
      }
    })();
    return () => {
      active = false;
    };
  }, [booking.quoteId, booking.proposedStartTime, booking.proposedEndTime, booking.startTime, booking.endTime, booking.locationName, booking.address, booking.mapsUrl]);

  const [noticeHint, setNoticeHint] = useState('');
  const [failedNoticeId, setFailedNoticeId] = useState('');
  const [mailAsk, setMailAsk] = useState<{ body: Record<string, unknown>; told: string; quiet: string } | null>(null);
  const [channelPrompt, setChannelPrompt] = useState<{
    emailStatus?: string;
    kind: 'confirmed' | 'updated';
    shootDate?: string;
    startTime?: string;
    locationName?: string;
    address?: string;
    mapsUrl?: string;
    quoteNumber?: string;
    link?: string;
    sessions?: Booking['sessions'];
  } | null>(null);

  const describeNotice = (notice: { status?: string; duplicate?: boolean; id?: string; type?: string } | null | undefined) => {
    if (!notice) return '';
    if (notice.duplicate) return 'Client update already recorded for this booking change.';
    if (notice.status === 'sent') return 'Client update emailed.';
    if (notice.status === 'queued') return 'Client update queued for email.';
    if (notice.status === 'captured') return 'Client update saved locally. Email is disabled in this environment.';
    if (notice.status === 'not_configured') return 'Client update stored. Email delivery is not configured.';
    if (notice.status === 'failed') return 'Booking saved, but the client email failed.';
    return `Client update status: ${notice.status}.`;
  };

  const send = async (body: Record<string, unknown>, note: string) => {
    setError('');
    setNoticeHint('');
    setFailedNoticeId('');
    setChannelPrompt(null);
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${booking.quoteId}/booking`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timezone: booking.timezone || 'Africa/Johannesburg', locationName: booking.locationName || '', ...body }),
    });
    const saved = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(saved.error || 'The date could not be saved.');
      return;
    }
    const nextBooking = { ...booking, ...(saved.booking || {}) };
    const notice = saved.notice || saved.booking?.clientNotice || null;
    const noticeLine = describeNotice(notice);
    if (notice?.status === 'failed' && notice.id) setFailedNoticeId(notice.id);
    setNoticeHint(noticeLine);
    onSaved(nextBooking, noticeLine ? `${note} ${noticeLine}` : note);
    // Email already auto-queued for confirmed client-visible changes — offer WhatsApp only (no second email).
    if (notice && !notice.duplicate && notice.type) {
      setChannelPrompt({
        emailStatus: notice.status,
        kind: notice.type === 'shoot-confirmed' ? 'confirmed' : 'updated',
        shootDate: nextBooking.shootDate,
        startTime: nextBooking.startTime,
        locationName: nextBooking.locationName,
        address: nextBooking.address,
        mapsUrl: nextBooking.mapsUrl,
        quoteNumber: nextBooking.quoteNumber || booking.quoteNumber,
        link: notice.link || saved.clientLink,
        sessions: nextBooking.sessions,
      });
    }
  };

  const retryNotice = async () => {
    if (!failedNoticeId) return;
    const response = await fetch(`${API_BASE_URL}/studio/notices/${failedNoticeId}/retry`, { method: 'POST' });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'The client update could not be retried.');
      return;
    }
    setNoticeHint(describeNotice(body));
    if (body.status !== 'failed') setFailedNoticeId('');
  };

  const onFollowUpHandledRef = useRef(onFollowUpHandled);
  onFollowUpHandledRef.current = onFollowUpHandled;
  useEffect(() => {
    if (!followUp) return;
    if (followUp.noticeLine) setNoticeHint(followUp.noticeLine);
    if (followUp.failedNoticeId) setFailedNoticeId(followUp.failedNoticeId);
    setChannelPrompt({
      emailStatus: followUp.emailStatus,
      kind: followUp.kind,
      shootDate: followUp.shootDate,
      startTime: followUp.startTime,
      locationName: followUp.locationName,
      address: followUp.address,
      mapsUrl: followUp.mapsUrl,
      quoteNumber: followUp.quoteNumber,
      link: followUp.link,
      sessions: followUp.sessions,
    });
    onFollowUpHandledRef.current?.();
  }, [followUp]);

  const askToEmail = (body: Record<string, unknown>, savedNote: string) => {
    setMailAsk({
      body,
      told: savedNote,
      quiet: `${savedNote} The client was not emailed.`,
    });
  };

  const timeOptions = ensureTimeOption(proposalStart);
  const durationOptions = ensureDurationOption(durationMinutes);
  const waitingChange = Boolean(booking.dateConfirmed && booking.proposedShootDate);
  const confirmLabel = booking.dateConfirmed && !waitingChange ? 'Update shoot' : 'Confirm shoot';

  return (
    <aside className={`studio-detail${embedded ? ' studio-detail-embedded' : ''}`} aria-label="Booking detail">
      <div>
        {embedded ? null : (
          <>
            <h3>{booking.clientName || 'Client'}</h3>
            <p>{booking.quoteNumber}</p>
            <p>{bookingState(booking)}</p>
          </>
        )}
        {flags(booking).map((flag) => <p className="studio-flag" key={flag}>{flag}</p>)}
        {booking.clash ? <p className="studio-error">This shoot overlaps another confirmed booking.</p> : null}
        {booking.blocked ? <p className="studio-error">This date is inside a blocked period{booking.blockReason ? `: ${booking.blockReason}` : ''}.</p> : null}
        {onOpenQuotation && !embedded ? <button type="button" className="studio-text-action" onClick={() => onOpenQuotation(booking.quoteId)}>Open shoot</button> : null}
      </div>
      <div>
        <p className="studio-shoot-state" data-testid="shoot-state">
          {Array.isArray(booking.sessions) && booking.sessions.length > 1 ? (
            <><strong>Schedule:</strong> {booking.sessions.map((session, index) => (
              <span key={session.id || index} style={{ display: 'block' }}>
                {session.label || `Day ${index + 1}`}: {studioDate(session.shootDate) || '—'} {session.startTime || ''}{session.endTime ? `–${session.endTime}` : ''}
              </span>
            ))}</>
          ) : booking.dateConfirmed && booking.shootDate
            ? <><strong>Confirmed:</strong> {studioDate(booking.shootDate)} {booking.startTime || ''}{booking.endTime ? `–${booking.endTime}` : ''}</>
            : <strong>No date confirmed yet.</strong>}
        </p>
        {waitingChange ? <p className="studio-note">This change is not confirmed yet. The client keeps the date above until you confirm.</p> : null}
        {embedded ? null : <p>{booking.timezone || 'Africa/Johannesburg'}</p>}
        {days ? null : (
        <div className="studio-stage-fields">
          <PlaceSearch onSelect={(place) => { setLocationName(place.name); setAddress(place.address); setMapsUrl(place.mapsUrl); }} />
          <label>
            Place
            <input aria-label="Place" value={locationName} onChange={(event) => setLocationName(event.target.value)} />
          </label>
          <label>
            Address
            <input aria-label="Address" value={address} onChange={(event) => setAddress(event.target.value)} />
          </label>
          {(locationName || address) ? (
            <p className="studio-note">
              <a
                href={mapsUrl && /^https:\/\//i.test(mapsUrl)
                  ? mapsUrl
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([locationName, address].filter(Boolean).join(', '))}`}
                target="_blank"
                rel="noreferrer"
              >
                Open in Google Maps
              </a>
            </p>
          ) : null}
          {(locationName || address || mapsUrl) ? <ShootMap mapsUrl={mapsUrl} label={locationName || address} /> : null}
        </div>
        )}
        {embedded ? null : <p>{booking.shootType}</p>}
        {packageDurationMinutes ? <p className="studio-note">Package duration {formatDurationLabel(packageDurationMinutes)}.</p> : null}
        {error ? <p className="studio-error" role="alert">{error}</p> : null}
        {!days && !booking.dateConfirmed && !proposalDate ? (
          <p className="studio-note" data-testid="requested-date">
            {requestedDate ? (
              <>
                Client asked for {formatPreferredDate(requestedDate)}{suggestedTime ? ` at ${suggestedTime}` : ''}.{' '}
                <button type="button" className="studio-text-action" onClick={() => setProposalDate(requestedDate)}>Use this date</button>
              </>
            ) : 'Client has not chosen a date yet.'}
          </p>
        ) : null}
        {days ? (
          <div className="studio-stage-fields studio-confirm-days" data-testid="confirm-days">
            {days.map((day, index) => {
              const end = addMinutesToClock(day.startTime, day.durationMinutes);
              const updateDay = (patch: Partial<DayDraft>) => {
                daysTouched.current = true;
                setDays((current) => (
                  current
                    ? current.map((item, itemIndex) => (
                      itemIndex === index ? { ...item, ...patch, confirmed: false } : item
                    ))
                    : current
                ));
              };
              const confirmThisDay = () => {
                const next = days.map((item, itemIndex) => (
                  itemIndex === index ? { ...item, confirmed: true } : item
                ));
                const everyDay = next.every((item) => item.confirmed && item.date && item.startTime);
                daysTouched.current = true;
                setDays(next);
                const first = next[0];
                const body = {
                  shootDate: first.date,
                  startTime: first.startTime,
                  endTime: addMinutesToClock(first.startTime, first.durationMinutes),
                  locationName: first.locationName.trim(),
                  address: first.address.trim(),
                  mapsUrl: first.mapsUrl.trim(),
                  sessions: next.map((item) => ({
                    id: item.id,
                    label: item.label,
                    date: item.date,
                    shootDate: item.date,
                    startTime: item.startTime,
                    durationMinutes: item.durationMinutes,
                    locationName: item.locationName.trim(),
                    address: item.address.trim(),
                    mapsUrl: item.mapsUrl.trim(),
                    confirmed: item.confirmed,
                    location: {
                      name: item.locationName.trim(),
                      address: item.address.trim(),
                      mapsUrl: item.mapsUrl.trim(),
                    },
                  })),
                  dateConfirmed: everyDay,
                  applyConfirmed: Boolean(booking.dateConfirmed) && everyDay,
                };
                if (!everyDay) {
                  send(
                    { ...body, notifyClient: false },
                    `${day.label} is confirmed. The client is told once every day is confirmed.`,
                  );
                  return;
                }
                askToEmail(body, booking.dateConfirmed ? 'Shoot updated.' : 'Every day is confirmed.');
              };
              return (
                <div key={day.id} className="studio-confirm-day" data-testid={`confirm-day-${index}`}>
                  <p className="studio-section-label">{day.label}</p>
                  <PlaceSearch onSelect={(place) => updateDay({ locationName: place.name, address: place.address, mapsUrl: place.mapsUrl })} />
                  <label>
                    Place
                    <input aria-label={`Place for ${day.label}`} value={day.locationName} onChange={(event) => updateDay({ locationName: event.target.value })} />
                  </label>
                  <label>
                    Address
                    <input aria-label={`Address for ${day.label}`} value={day.address} onChange={(event) => updateDay({ address: event.target.value })} />
                  </label>
                  {(day.locationName || day.address || day.mapsUrl) ? <ShootMap mapsUrl={day.mapsUrl} label={day.locationName || day.address} /> : null}
                  <div className="studio-stage-when">
                    <label>
                      Date
                      <input aria-label={`Date for ${day.label}`} type="date" value={day.date} onChange={(event) => updateDay({ date: event.target.value })} />
                    </label>
                    <label>
                      Start
                      <select aria-label={`Start time for ${day.label}`} value={day.startTime} onChange={(event) => updateDay({ startTime: event.target.value })}>
                        <option value="">Start time</option>
                        {ensureTimeOption(day.startTime).map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Duration
                      <select aria-label={`Duration for ${day.label}`} value={String(day.durationMinutes)} onChange={(event) => updateDay({ durationMinutes: Number(event.target.value) })}>
                        {ensureDurationOption(day.durationMinutes).map((minutes) => (
                          <option key={minutes} value={minutes}>{formatDurationLabel(minutes)}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p className="studio-note" aria-live="polite">{day.startTime && end ? `Ends ${end}` : 'Choose a start time'}</p>
                  <button
                    type="button"
                    className="studio-primary"
                    data-testid={`confirm-day-button-${index}`}
                    disabled={!day.date || !day.startTime || day.confirmed}
                    onClick={confirmThisDay}
                  >
                    {day.confirmed ? `${day.label} confirmed` : `Confirm ${day.label}`}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}
        {days ? null : (
        <div className="studio-stage-fields">
          <div className="studio-stage-when">
            <label>
              Date
              <input aria-label="Shoot date" type="date" value={proposalDate} onChange={(event) => setProposalDate(event.target.value)} />
            </label>
            <label>
              Start
              <select
                aria-label="Start time"
                value={proposalStart}
                onChange={(event) => {
                  startEdited.current = true;
                  setProposalStart(event.target.value);
                }}
              >
                <option value="">Start time</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </label>
            <label>
              Duration
              <select
                aria-label="Duration"
                value={String(durationMinutes)}
                onChange={(event) => setDurationMinutes(Number(event.target.value))}
              >
                {durationOptions.map((minutes) => (
                  <option key={minutes} value={minutes}>{formatDurationLabel(minutes)}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="studio-note" aria-live="polite">{proposalStart && proposalEnd ? `Ends ${proposalEnd}` : 'Choose a start time'}</p>
          <button
            type="button"
            className="studio-primary"
            data-testid="confirm-shoot"
            disabled={!proposalDate || !proposalStart}
            onClick={() => {
              const existingSessions = Array.isArray(booking.sessions) ? booking.sessions : [];
              const nextSessions = existingSessions.length > 1
                ? existingSessions.map((session, index) => (
                  index === 0 || session.id === booking.sessionId
                    ? {
                        ...session,
                        shootDate: proposalDate,
                        startTime: proposalStart,
                        endTime: proposalEnd,
                        durationMinutes: durationMinutes,
                        locationName: locationName.trim() || session.locationName || '',
                        address: address.trim() || session.address || '',
                        mapsUrl: mapsUrl.trim() || session.mapsUrl || '',
                      }
                    : session
                ))
                : existingSessions.length === 1
                  ? [{
                      ...existingSessions[0],
                      shootDate: proposalDate,
                      startTime: proposalStart,
                      endTime: proposalEnd,
                      durationMinutes,
                      locationName: locationName.trim(),
                      address: address.trim(),
                      mapsUrl: mapsUrl.trim(),
                    }]
                  : [{
                      id: 'session-1',
                      label: '',
                      shootDate: proposalDate,
                      startTime: proposalStart,
                      endTime: proposalEnd,
                      durationMinutes,
                      locationName: locationName.trim(),
                      address: address.trim(),
                      mapsUrl: mapsUrl.trim(),
                    }];
              askToEmail(
                {
                  shootDate: proposalDate,
                  startTime: proposalStart,
                  endTime: proposalEnd,
                  locationName: locationName.trim(),
                  address: address.trim(),
                  mapsUrl: mapsUrl.trim(),
                  sessions: nextSessions,
                  dateConfirmed: true,
                  applyConfirmed: Boolean(booking.dateConfirmed),
                },
                booking.dateConfirmed ? 'Shoot updated.' : 'Shoot confirmed.',
              );
            }}
          >
            {confirmLabel}
          </button>
          {booking.proposedShootDate ? <button type="button" className="studio-secondary" onClick={() => send({ discardProposed: true }, 'The proposed date was discarded.')}>Discard change</button> : null}
        </div>
        )}
        <p className="studio-note">
          {days
            ? 'Each day is its own block, filled from the quotation. Confirm each day on its own. You are asked about emailing the client only once every day is confirmed.'
            : booking.dateConfirmed && !waitingChange
              ? 'Change the date, time, duration, or place, then update. You will be asked before the client is emailed.'
              : 'Change the date, time, duration, or place above, then confirm. You will be asked before the client is emailed.'}
        </p>
        {noticeHint ? <p className="studio-note" data-testid="booking-notice-status">{noticeHint}</p> : null}
        {failedNoticeId ? (
          <button type="button" className="studio-secondary" data-testid="retry-booking-notice" onClick={retryNotice}>
            Retry client email
          </button>
        ) : null}
        {mailAsk ? (
          <ClientSendPrompt
            title="Email the client?"
            detail={mailAsk.told.startsWith('Shoot updated')
              ? 'This change can be emailed to the client.'
              : 'The confirmation can be emailed to the client.'}
            onSend={() => {
              const choice = mailAsk;
              setMailAsk(null);
              void send({ ...choice.body, notifyClient: true }, choice.told);
            }}
            onSkip={() => {
              const choice = mailAsk;
              setMailAsk(null);
              void send({ ...choice.body, notifyClient: false }, choice.quiet);
            }}
          />
        ) : null}
        {channelPrompt ? (
          <NotifyChannelPrompt
            emailAlreadyQueued
            emailStatus={channelPrompt.emailStatus}
            preference={clientPreference}
            hasEmail={Boolean(clientEmail)}
            hasPhone={Boolean(clientPhone)}
            whatsAppUrl={buildStudioWhatsAppUrl(
              clientPhone,
              bookingUpdateWhatsAppText({
                quoteNumber: channelPrompt.quoteNumber,
                shootDate: channelPrompt.shootDate,
                startTime: channelPrompt.startTime,
                locationName: channelPrompt.locationName,
                address: channelPrompt.address,
                mapsUrl: channelPrompt.mapsUrl,
                durationMinutes: packageDurationMinutes,
                link: channelPrompt.link,
                kind: channelPrompt.kind,
                sessions: channelPrompt.sessions,
              }),
            )}
            onDone={(summary) => {
              setNoticeHint([noticeHint, ...summary.lines].filter(Boolean).join(' '));
              setChannelPrompt(null);
            }}
            onDismiss={() => setChannelPrompt(null)}
          />
        ) : null}
      </div>
    </aside>
  );
};

// The grid starts below the admin bar, title, view tabs, and legend.
// Keep it inside the window so a month or week can be read without zooming.
const calendarFrameHeight = () => {
  if (typeof window === 'undefined') return 520;
  return Math.max(380, Math.min(620, window.innerHeight - 330));
};

const StudioBookings = ({ onOpenQuotation }: { onOpenQuotation?: (quoteId: string) => void }) => {
  const calendarRef = useRef<any>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [unscheduled, setUnscheduled] = useState<Array<{ quoteId: string; quoteNumber: string; clientName: string; shootType: string }>>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [view, setView] = useState<ViewName>(() => window.matchMedia('(max-width: 760px)').matches ? 'agenda' : 'dayGridMonth');
  const [selected, setSelected] = useState<Booking | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [frameHeight, setFrameHeight] = useState(calendarFrameHeight);

  useEffect(() => {
    const fit = () => setFrameHeight(calendarFrameHeight());
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const load = async (preferId?: string) => {
    setError('');
    const response = await fetch(`${API_BASE_URL}/studio/bookings`, { cache: 'no-store' });
    if (!response.ok) {
      setError('Bookings could not be loaded.');
      return;
    }
    const body = await response.json();
    const next = Array.isArray(body.bookings) ? body.bookings as Booking[] : [];
    setBookings(next);
    setUnscheduled(Array.isArray(body.unscheduled) ? body.unscheduled : []);
    setBlocks(Array.isArray(body.blocks) ? body.blocks : []);
    if (preferId) {
      const match = next.find((booking) => booking.quoteId === preferId);
      if (match) setSelected(match);
    }
  };

  useEffect(() => {
    load().catch(() => setError('Bookings could not be loaded.'));
  }, []);

  useEffect(() => {
    if (view !== 'dayGridMonth' && view !== 'timeGridWeek') return;
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.changeView(view);
  }, [view]);

  const hours = useMemo(() => {
    let earliest = 7 * 60;
    let latest = 19 * 60;
    bookings.forEach((booking) => {
      const start = minutes(booking.startTime);
      if (start == null) return;
      const end = minutes(booking.endTime) ?? start + 60;
      earliest = Math.min(earliest, Math.floor(start / 60) * 60);
      latest = Math.max(latest, Math.ceil(end / 60) * 60);
    });
    return { slotMinTime: clock(earliest), slotMaxTime: clock(Math.max(latest, earliest + 60)) };
  }, [bookings]);

  const events = useMemo(() => {
    const shoots = bookings.filter((booking) => booking.shootDate && !booking.deletedAt && !booking.archivedAt).map((booking) => {
      const marks = flags(booking);
      const chip = eventChip(booking);
      const longTitle = [
        booking.clientName || 'Client',
        booking.shootType || 'Shoot',
        chip.time ? `${chip.time}${booking.endTime ? `–${String(booking.endTime).slice(0, 5)}` : ''}` : '',
        booking.locationName || '',
        bookingState(booking),
        ...marks,
      ].filter(Boolean).join(' · ');
      return {
        id: booking.sessionId ? `${booking.quoteId}:${booking.sessionId}` : booking.quoteId,
        title: eventLabel(booking),
        start: `${booking.shootDate}T${booking.startTime || '09:00'}`,
        end: booking.endTime ? `${booking.shootDate}T${booking.endTime}` : undefined,
        classNames: appointmentTone(booking),
        extendedProps: { ...booking, longTitle },
      };
    });
    const shaded = blocks.map((block) => ({
      id: `block-${block.id}`,
      start: block.startDate,
      end: nextDay(block.endDate),
      display: 'background' as const,
      title: `Blocked${block.reason ? ` · ${block.reason}` : ''}`,
      className: 'is-block',
    }));
    const today = studioToday();
    const editing = bookings
      .filter((booking) => !booking.deletedAt && !booking.archivedAt)
      .flatMap((booking) => editingEvents(booking, today));
    return [...shoots, ...editing, ...shaded];
  }, [bookings, blocks]);

  const awaitingDelivery = useMemo(() => {
    const today = studioToday();
    return bookings
      .filter((booking) => isEditing(booking) && !booking.deletedAt && !booking.archivedAt)
      .map((booking) => ({ booking, countdown: deliveryCountdown(booking.deliveryDueOn, today) }))
      .sort((left, right) => (left.countdown.daysLeft ?? 9999) - (right.countdown.daysLeft ?? 9999));
  }, [bookings]);

  const chooseView = (next: ViewName) => {
    setView(next);
    if (next === 'dayGridMonth' || next === 'timeGridWeek') calendarRef.current?.getApi().changeView(next);
  };

  const move = (direction: 'prev' | 'next' | 'today') => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api[direction]();
    setTitle(api.view.title);
  };

  const onKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const index = views.findIndex(([id]) => id === view);
    const step = event.key === 'ArrowRight' ? 1 : -1;
    chooseView(views[(index + step + views.length) % views.length][0]);
    event.preventDefault();
  };

  const remember = async (booking: Booking, note: string) => {
    setSelected(booking);
    setMessage(note);
    await load(booking.quoteId);
  };

  const openJob = (booking: Booking) => {
    if (booking.quoteId && onOpenQuotation) {
      onOpenQuotation(booking.quoteId);
      return;
    }
    setSelected(booking);
  };

  const hasEditing = awaitingDelivery.some(({ booking }) => /^\d{4}-\d{2}-\d{2}$/.test(String(booking.deliveryDueOn || '')));
  const visibleList = bookings.filter((booking) => !booking.deletedAt && !booking.archivedAt);
  const agenda = visibleList.filter((booking) => booking.shootDate).sort((left, right) => `${left.shootDate} ${left.startTime}`.localeCompare(`${right.shootDate} ${right.startTime}`));
  const calendarVisible = view === 'dayGridMonth' || view === 'timeGridWeek';

  return (
    <div className="studio-home" data-testid="studio-bookings">
      <header className="studio-home-head">
        <div>
          <h2>Calendar</h2>
          <p>Who is booked, when, and whether the date is confirmed. Click a shoot to open the job.</p>
        </div>
      </header>
      <div className="studio-calendar-chrome" onKeyDown={onKey}>
        <div className="studio-view-tabs" role="group" aria-label="Booking views">
          {views.map(([id, label]) => (
            <button key={id} type="button" className={view === id ? 'is-active' : ''} aria-pressed={view === id} onClick={() => chooseView(id)}>{label}</button>
          ))}
        </div>
        {calendarVisible ? (
          <div className="studio-calendar-nav" role="group" aria-label="Calendar navigation">
            <button type="button" className="studio-secondary" onClick={() => move('prev')} aria-label="Previous period">‹</button>
            <button type="button" className="studio-secondary" onClick={() => move('today')}>Today</button>
            <button type="button" className="studio-secondary" onClick={() => move('next')} aria-label="Next period">›</button>
            <strong className="studio-calendar-title" data-testid="calendar-period-title">{title || 'Loading…'}</strong>
          </div>
        ) : null}
      </div>
      <ul className="studio-cal-legend" aria-label="Appointment types">
        <li><i className="studio-cal-swatch is-proposed" aria-hidden="true" />Proposed</li>
        <li><i className="studio-cal-swatch is-confirmed" aria-hidden="true" />Confirmed</li>
        <li><i className="studio-cal-swatch is-complete" aria-hidden="true" />Shoot done</li>
        <li><i className="studio-cal-swatch is-delivery-due" aria-hidden="true" />Photographs due</li>
        <li><i className="studio-cal-swatch is-overlap" aria-hidden="true" />Overlap</li>
        <li><i className="studio-cal-swatch is-blocked" aria-hidden="true" />Blocked</li>
      </ul>
      {awaitingDelivery.length > 0 ? (
        <section className="studio-panel studio-editing-list" aria-label="Awaiting delivery" data-testid="awaiting-delivery">
          <h3 className="studio-section-label">Editing · clients waiting</h3>
          <p className="studio-note">The red dot on the calendar is the day the photographs are due. Roll over it for who is waiting.</p>
          {awaitingDelivery.map(({ booking, countdown }) => (
            <button
              type="button"
              className={['studio-record', 'is-editing', countdown.overdue ? 'is-overdue' : ''].filter(Boolean).join(' ')}
              key={booking.quoteId}
              onClick={() => openJob(booking)}
              data-testid={`awaiting-delivery-${booking.quoteId}`}
            >
              <strong>{booking.clientName || 'Client'}{booking.isTest ? ' · Test' : ''}</strong>
              <span>{booking.shootType || 'Shoot'} · shot {studioDate(booking.shootDate) || '—'}{booking.deliveryDueOn ? ` · due ${studioDate(booking.deliveryDueOn)}` : ''}</span>
              <em className="studio-days-left">{booking.deliveryDueOn ? countdown.text : 'No delivery date set — open the job to add one'}</em>
            </button>
          ))}
        </section>
      ) : null}
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {message ? <p className="studio-note">{message}</p> : null}
      <div className={`studio-calendar studio-panel${calendarVisible ? '' : ' is-hidden'}`}>
        <FullCalendar
          ref={calendarRef}
          plugins={[classicThemePlugin, dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView="dayGridMonth"
          timeZone="Africa/Johannesburg"
          headerToolbar={false}
          events={events}
          height={frameHeight}
          fixedWeekCount={false}
          dayMaxEvents={view === 'dayGridMonth' ? 2 : 4}
          moreLinkClick="popover"
          displayEventTime={false}
          displayEventEnd={false}
          eventDisplay="block"
          slotDuration="01:00:00"
          slotMinTime={hours.slotMinTime}
          slotMaxTime={hours.slotMaxTime}
          scrollTime={hours.slotMinTime}
          allDaySlot={hasEditing}
          allDayText="Editing"
          nowIndicator
          expandRows
          backgroundEventDidMount={(info) => {
            const booking = info.event.extendedProps as Booking & { longTitle?: string; editingKind?: string; overdue?: boolean };
            if (booking.editingKind !== 'block') return;
            info.el.classList.add('is-editing-block');
            if (booking.overdue) info.el.classList.add('is-overdue');
            if (booking.longTitle) info.el.setAttribute('title', booking.longTitle);
          }}
          eventContent={(arg) => {
            const booking = arg.event.extendedProps as Booking & { editingKind?: 'block' | 'dot' | 'due' };
            if (booking.editingKind === 'block') return undefined;
            if (booking.editingKind === 'dot' || booking.editingKind === 'due') {
              return { html: '<span class="studio-cal-dot" aria-hidden="true"></span>' };
            }
            if (!booking?.quoteId) {
              return { html: `<span class="studio-cal-event-label">${escapeHtml(arg.event.title)}</span>` };
            }
            const chip = eventChip(booking);
            return {
              html: `<span class="studio-cal-event-label"><strong>${escapeHtml(chip.name)}</strong><span>${escapeHtml([chip.time, chip.state, chip.shoot, ...chip.warnings].filter(Boolean).join(' · '))}</span></span>`,
            };
          }}
          eventDidMount={(info) => {
            const booking = info.event.extendedProps as Booking & { longTitle?: string; editingKind?: string; overdue?: boolean; editingOn?: string };
            if (booking.longTitle) info.el.setAttribute('title', booking.longTitle);
            if (booking.editingKind === 'block') {
              info.el.classList.add('is-editing-block');
              if (booking.overdue) info.el.classList.add('is-overdue');
              return;
            }
            if (!booking.quoteId) return;
            // FullCalendar CSS-modules build hashes .fc-event; stamp our own surface for chip chrome.
            info.el.classList.add('studio-cal-event');
            if (booking.editingKind === 'dot' || booking.editingKind === 'due') {
              info.el.classList.add('is-editing-dot');
              if (booking.editingKind === 'due') info.el.classList.add('is-delivery-dot');
              if (booking.overdue) info.el.classList.add('is-overdue');
              const stamp = booking.editingKind === 'due' ? 'deliver' : 'editing';
              info.el.setAttribute('data-testid', `cal-${stamp}-${booking.quoteId}${booking.editingOn ? `-${booking.editingOn}` : ''}`);
              return;
            }
            appointmentTone(booking).forEach((name) => info.el.classList.add(name));
          }}
          datesSet={(info) => {
            if (info.view.type === 'timeGridWeek') {
              const end = new Date(info.end.getTime() - 86400000);
              const sameMonth = info.start.getMonth() === end.getMonth() && info.start.getFullYear() === end.getFullYear();
              const day = new Intl.DateTimeFormat('en-ZA', { day: 'numeric' });
              const full = new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
              setTitle(sameMonth
                ? `${day.format(info.start)}–${day.format(end)} ${new Intl.DateTimeFormat('en-ZA', { month: 'long', year: 'numeric' }).format(end)}`
                : `${new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'long' }).format(info.start)} – ${full.format(end)}`);
              return;
            }
            setTitle(info.view.title);
          }}
          eventClick={(info) => {
            const booking = info.event.extendedProps as Booking & { editingKind?: string };
            if (booking.editingKind === 'block') return;
            if (booking.quoteId) openJob(booking);
          }}
        />
      </div>
      {view === 'agenda' ? (
        <section className="studio-panel studio-agenda-list">
          {agenda.length === 0 ? <p className="studio-empty">No bookings scheduled.</p> : agenda.map((booking) => (
            <button type="button" className={['studio-record', ...appointmentTone(booking), isEditing(booking) ? 'is-editing' : ''].filter(Boolean).join(' ')} key={booking.quoteId} onClick={() => openJob(booking)}>
              <strong>{booking.clientName}</strong>
              <span>{studioDate(booking.shootDate)} · {booking.startTime || 'Time not set'} · {booking.shootType || 'Shoot'}</span>
              <em>{[bookingState(booking), isEditing(booking) && booking.deliveryDueOn ? deliveryCountdown(booking.deliveryDueOn).text : '', ...flags(booking)].filter(Boolean).join(' · ')}</em>
            </button>
          ))}
        </section>
      ) : null}
      {view === 'list' ? (
        <div className="studio-table-wrap">
          <table className="studio-table">
            <thead>
              <tr><th>Date</th><th>Client</th><th>Shoot</th><th>Time</th><th>Status</th></tr>
            </thead>
            <tbody>
              {visibleList.length === 0 ? <tr><td colSpan={5}>No bookings scheduled.</td></tr> : visibleList.map((booking) => (
                <tr key={booking.quoteId} onClick={() => openJob(booking)}>
                  <td data-label="Date">{studioDate(booking.shootDate) || 'Unscheduled'}</td>
                  <td data-label="Client">{booking.clientName}</td>
                  <td data-label="Shoot">{booking.shootType}</td>
                  <td data-label="Time">{booking.startTime || ''}</td>
                  <td data-label="Status">
                    {isEditing(booking) ? <i className="studio-cal-swatch is-editing" aria-hidden="true" /> : appointmentTone(booking).length ? <i className={['studio-cal-swatch', ...appointmentTone(booking)].join(' ')} aria-hidden="true" /> : null}
                    {[bookingState(booking), ...flags(booking)].join(' · ')}
                    {isEditing(booking) && booking.deliveryDueOn ? <div className="studio-row-sub studio-days-left">{deliveryCountdown(booking.deliveryDueOn).text}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {view === 'unscheduled' ? (
        <section className="studio-panel">
          <h3 className="studio-section-label">Accepted, no date</h3>
          {unscheduled.length === 0 ? <p className="studio-empty">No bookings are waiting for a date.</p> : unscheduled.map((item) => (
            <button type="button" className="studio-record" key={item.quoteId} onClick={() => openJob({ quoteId: item.quoteId, quoteNumber: item.quoteNumber, clientName: item.clientName, shootType: item.shootType, timezone: 'Africa/Johannesburg' })}>
              <strong>{item.clientName || 'Client'}</strong>
              <span>{item.quoteNumber} · {item.shootType || 'Shoot'}</span>
              <em>Needs a date · a deposit does not confirm a date</em>
            </button>
          ))}
        </section>
      ) : null}
      <section className="studio-panel">
        <h3 className="studio-section-label">Blocked periods</h3>
        <div className="studio-toolbar">
          <input aria-label="Block starts" type="date" value={blockStart} onChange={(event) => setBlockStart(event.target.value)} />
          <input aria-label="Block ends" type="date" value={blockEnd} onChange={(event) => setBlockEnd(event.target.value)} />
          <input aria-label="Block reason" value={blockReason} onChange={(event) => setBlockReason(event.target.value)} placeholder="Reason" />
          <button type="button" className="studio-secondary" onClick={async () => {
            const response = await fetch(`${API_BASE_URL}/studio/blocks`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ startDate: blockStart, endDate: blockEnd || blockStart, reason: blockReason }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
              setError(body.error || 'The blocked period could not be saved.');
              return;
            }
            setBlockStart('');
            setBlockEnd('');
            setBlockReason('');
            await load(selected?.quoteId);
          }}>Block dates</button>
        </div>
        {blocks.map((block) => (
          <div className="studio-activity-row" key={block.id}>
            <div>{studioDate(block.startDate)}</div>
            <div>{studioDate(block.endDate)} {block.reason} <button type="button" className="studio-text-action" onClick={() => fetch(`${API_BASE_URL}/studio/blocks/${block.id}`, { method: 'DELETE' }).then(() => load(selected?.quoteId))}>Remove</button></div>
          </div>
        ))}
      </section>
      {selected ? <BookingEditor booking={selected} onOpenQuotation={onOpenQuotation} onSaved={remember} /> : null}
    </div>
  );
};

export default StudioBookings;
